import {
  stripTemplateArtifactsFromDataUrl,
  type StripTemplateArtifactsOptions,
} from "@/character/strip-template-artifacts";

const STORAGE_KEY = "mind-survival/sheet-upload-clean/v1";

export type SheetUploadCleanPrefs = {
  removeWhite: boolean;
  removeBlack: boolean;
  removeGuides: boolean;
};

const DEFAULT_PREFS: SheetUploadCleanPrefs = {
  removeWhite: true,
  removeBlack: true,
  removeGuides: true,
};

export function getSheetUploadCleanPrefs(): SheetUploadCleanPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<SheetUploadCleanPrefs>;
    return {
      removeWhite: parsed.removeWhite !== false,
      removeBlack: parsed.removeBlack !== false,
      removeGuides: parsed.removeGuides !== false,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function setSheetUploadCleanPrefs(prefs: SheetUploadCleanPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

export function prefsToStripOptions(prefs: SheetUploadCleanPrefs): StripTemplateArtifactsOptions {
  return {
    removeWhite: prefs.removeWhite,
    removeBlack: prefs.removeBlack,
    removeGuides: prefs.removeGuides,
  };
}

export async function applySheetUploadClean(
  dataUrl: string,
  prefs = getSheetUploadCleanPrefs(),
): Promise<string> {
  return stripTemplateArtifactsFromDataUrl(dataUrl, prefsToStripOptions(prefs));
}

export function renderSheetUploadCleanControlsHtml(idPrefix = "sheet-clean"): string {
  const p = getSheetUploadCleanPrefs();
  return `
    <fieldset class="sheet-upload-clean" aria-label="ลบกรอบเทมเพลต">
      <legend class="sheet-upload-clean-legend">หลังอัปโหลด</legend>
      <label class="sheet-upload-clean-item">
        <input type="checkbox" id="${idPrefix}-white" data-sheet-clean="removeWhite" ${p.removeWhite ? "checked" : ""} />
        <span>ตัดพื้นขาว</span>
      </label>
      <label class="sheet-upload-clean-item">
        <input type="checkbox" id="${idPrefix}-black" data-sheet-clean="removeBlack" ${p.removeBlack ? "checked" : ""} />
        <span>ตัดพื้นดำ (เซลล์เทมเพลต)</span>
      </label>
      <label class="sheet-upload-clean-item">
        <input type="checkbox" id="${idPrefix}-guides" data-sheet-clean="removeGuides" ${p.removeGuides ? "checked" : ""} />
        <span>ตัดเส้นกรอบ / เส้นปะ</span>
      </label>
    </fieldset>
  `;
}

export function readSheetUploadCleanPrefsFromHost(host: HTMLElement): SheetUploadCleanPrefs {
  const read = (key: keyof SheetUploadCleanPrefs) =>
    host.querySelector<HTMLInputElement>(`[data-sheet-clean="${key}"]`)?.checked ?? true;
  return {
    removeWhite: read("removeWhite"),
    removeBlack: read("removeBlack"),
    removeGuides: read("removeGuides"),
  };
}

export function bindSheetUploadCleanControls(
  host: HTMLElement,
  onChange?: (prefs: SheetUploadCleanPrefs) => void,
): () => void {
  const sync = () => {
    const prefs = readSheetUploadCleanPrefsFromHost(host);
    setSheetUploadCleanPrefs(prefs);
    onChange?.(prefs);
    window.dispatchEvent(
      new CustomEvent("sheet-upload-clean-changed", { detail: { prefs } }),
    );
  };

  host.querySelectorAll<HTMLInputElement>("[data-sheet-clean]").forEach((input) => {
    input.addEventListener("change", sync);
  });

  return () => {
    host.querySelectorAll<HTMLInputElement>("[data-sheet-clean]").forEach((input) => {
      input.replaceWith(input.cloneNode(true));
    });
  };
}
